import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, CheckCircle2 } from 'lucide-react';
import Seo from './Seo';



const slides = [
  {
    image: '/Car1.jpg',
    heading: 'Leading the way in IT Innovation and Excellence',
    subheading: 'Discover the full potential of your business with our enterprise IT solutions. Our expert team delivers adaptable IT solutions that align technology with your planned goals.'
  },
  {
    image: '/Car2.jpg',
    heading: "Scale Your Vision with Strategic Technical Talent",
    subheading: "Empower your projects with high-performance managed teams and specialized expertise. We provide the agility and skills needed to accelerate your digital journey and exceed expectations."
  }
];



/* ─── COMPONENT ─────────────────────────────────────────────────────────── */

const Home = ({ scrollTarget, setScrollTarget }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    if (scrollTarget) {
      const element = document.getElementById(scrollTarget);
      if (element) {
        // Adjust offset for fixed header
        const headerOffset = 85;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
      // Reset scroll target after scrolling
      if (setScrollTarget) {
        setScrollTarget(null);
      }
    }
  }, [scrollTarget, setScrollTarget]);

  return (
    <div className="bg-white">
      <Seo
        title="Enterprise IT Solutions, AI, ERP & Staffing"
        description="Libsys Inc delivers enterprise IT services — AI, SAP ERP, business intelligence, cybersecurity, cloud, and strategic staffing — for banking, insurance, healthcare, retail, energy, and telecom."
        path="/"
      />

      {/* ══ HERO CAROUSEL ══════════════════════════════════════════════════════════ */}
      <section className="relative w-full h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        
        {/* Background Images */}
        <AnimatePresence initial={false}>
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 w-full h-full"
          >
            <img src={slides[currentSlide].image} alt={`Slide ${currentSlide + 1}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/40 via-transparent to-primary/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col items-center text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-5xl flex flex-col items-center"
            >
              <h1 className="text-[28px] md:text-[38px] text-white mb-6 drop-shadow-2xl font-black leading-[1.1] uppercase tracking-tight">
                {slides[currentSlide].heading}
              </h1>
              <p className="text-[15px] md:text-[16.5px] text-white/95 font-medium max-w-3xl leading-relaxed mb-10">
                {slides[currentSlide].subheading}
              </p>
              
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={prevSlide}
          className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 w-[52px] h-[52px] rounded-full border border-white/50 flex items-center justify-center text-white hover:bg-white/10 hover:border-white transition-all z-20"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 w-[52px] h-[52px] rounded-full border border-white/50 flex items-center justify-center text-white hover:bg-white/10 hover:border-white transition-all z-20"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Navigation Dots */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === i ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      </section>

      {/* ══ TRUST BADGES MARQUEE ══════════════════════════════════════════ */}
      <section className="py-12 border-b border-slate-100 bg-slate-50 overflow-hidden relative">
        <div className="max-w-[100vw] mx-auto">
          {/* Gradient fade overlays for the edges */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />

          <div className="flex w-fit group">
            {/* The continuous scrolling container */}
            <div className="animate-marquee flex items-center gap-16 px-8 whitespace-nowrap group-hover:[animation-play-state:paused]">
              {[...Array(2)].map((_, idx) => (
                <React.Fragment key={idx}>
                  {[
                    { title: "Inc 500 Fastest Growing Companies", img: "/award-logos/1.webp" },
                    { title: "Top Businesses in the U.S. by Diversity Business", img: "/award-logos/2.webp" },
                    { title: "Deloitte Technology Fast 500", img: "/award-logos/3.webp" },
                    { title: "CRN Fast Growth 100", img: "/award-logos/4.webp" },
                    { title: "Chicago's 101 Best and Brightest Companies", img: "/award-logos/5.webp" },
                    { title: "Top Software Services Firm in Mid-West", img: "/award-logos/6.webp" },
                    { title: "Top Indian American Entrepreneurs", img: "/award-logos/7.webp" }
                  ].map((badge, i) => (
                    <div
                      key={`${idx}-${i}`}
                      className="inline-flex items-center justify-center transition-all duration-300 flex-shrink-0"
                    >
                      <img src={badge.img} alt={badge.title} className="h-16 lg:h-20 w-auto object-contain drop-shadow-sm flex-shrink-0" />
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ IMPACT STRIP ═════════════════════════════════════════════════════ */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { value: '150+', label: 'Projects Delivered' },
              { value: '98%', label: 'Client Retention' },
              { value: '24/7', label: 'Delivery Support' },
              { value: '10+', label: 'Industry Verticals' }
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-4 text-left shadow-sm hover:shadow-md transition-all duration-400"
              >
                <p className="text-h3 text-primary">{item.value}</p>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mt-1">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PRE-FOOTER CTA ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#2e79a8] border border-[#236086]/20 shadow-xl p-5 lg:p-8">
            <div className="absolute inset-0 opacity-[0.2] bg-[radial-gradient(circle_at_20%_20%,white_0,transparent_45%)] pointer-events-none" />
            
            <div className="relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7 text-left">
                  <span className="text-red-tagline mb-2 inline-block opacity-90 text-tiny tracking-widest font-black uppercase">
                    Strategic Partnership
                  </span>
                  <h2 className="text-h2 text-white mb-3 leading-tight">
                    Scale your operations with a <span className="text-blue-100 italic">trusted</span> tech partner.
                  </h2>
                  <p className="text-blue-50/90 text-tiny leading-relaxed max-w-xl font-medium">
                    We deliver practical roadmaps, modernized systems, and enterprise-grade governance to improve productivity and create measurable long-term business value.
                  </p>
                  
                  <div className="mt-5 flex flex-col sm:flex-row gap-3 items-center">
                    <a
                      href="tel:+16307991556"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary hover:bg-blue-50 font-black text-[10px] uppercase tracking-wider rounded-lg transition-all shadow-lg active:scale-95 group"
                    >
                      Connect Us: 630-799-1556
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 space-y-3 shadow-inner max-w-lg">
                    {[
                      'Industry-aligned delivery frameworks',
                      'Specialized AI, SAP, and IT expertise',
                      'Performance-based project milestones'
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-blue-100 flex-shrink-0" />
                        <p className="text-white text-tiny font-bold leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



    </div>
  );
};

export default Home;